import React, { Fragment, useCallback, useEffect, useState } from "react";

import clsx from "clsx";
import { isEmpty, isNil } from "lodash";

import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { useUser } from "@components/user-context";
import {
  PrimaryButton,
  SecondaryButton,
  TertiaryButton,
} from "@components/v2/buttons";
import { FiltersButtonCompact } from "@features/sales-v3/minimized-top-bar/components/filters-button-compact";
import { BreakdownSelector } from "@features/sales-v3/shared";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { useFeatureFlags } from "@hooks/http";
import { prettyLogEvent } from "@internal/analytics";
import {
  CreativeMetricType,
  CreativesFilterClauseTypeEnum,
  CreativesFilterClauseValueOperatorsEnum,
  CreativesSortingOrder,
  CreativesStatusEnum,
  CreativesTypeEnum,
  type CreativesFilterMetricClauseInput,
  type CreativesMetricsFilterInput,
} from "@north-beam/nb-client-side";
import { Pages } from "@north-beam/nb-client-side/constants";
import { Checkbox } from "@shared/checkbox";
import { DebouncedTextInput } from "@shared/debounced-text-input";
import { Select } from "@shared/selects";
import { TextInput } from "@shared/text-input";

import { useAvailableMetricsByIdAtom, useCollapseTopBar } from "../atoms";
import {
  useReportBodyState,
  type CreativeSorting,
} from "../hooks/ca-control-state";

export function CreativeAnalyticsTypeBar() {
  const creativeTypes = useFetchCreativeTypes();
  const { user } = useUser();
  const { value: breakdownConfigs } = useSalesBreakdownConfigs();
  const { hasFeatureFlag } = useFeatureFlags();

  const {
    searchText,
    breakdownFilters,
    setSearchText,
    setBreakdownFilters,
    setActiveStatus,
    activeStatus,
    creativeType,
    setCreativeType,
    resetState,
  } = useReportBodyState();

  const activeCreative = React.useMemo(() => {
    return creativeTypes.find(({ id }) => {
      if (!creativeType && id === "all") {
        return true;
      }
      return id === creativeType;
    });
  }, [creativeType, creativeTypes]);

  return (
    <div className="flex flex-col h-[7rem] pl-2">
      <div className="flex flex-row items-center justify-between mt-4">
        <div className="flex flex-row ml-3 items-center gap-4">
          <DebouncedTextInput
            className="font-light text-sm bg-v2-primary border-v2-secondary"
            placeholder="Search campaign, adset or ad name"
            value={searchText}
            onChange={(val) => {
              setSearchText(val);
              resetState();
            }}
          />
          {hasFeatureFlag("enableBreakdownFiltersInCreativeAnalytics") && (
            <div className="flex items-center gap-2 text-xs">
              {breakdownFilters.length > 0 && (
                <FiltersButtonCompact
                  filters={breakdownFilters}
                  onUpdate={(filter) => {
                    const newFilters = breakdownFilters.map((e) =>
                      e.key === filter.key ? filter : e,
                    );

                    setBreakdownFilters(newFilters);
                  }}
                  onDelete={(filter) => {
                    setBreakdownFilters(
                      breakdownFilters.filter((e) => e.key !== filter.key),
                    );
                  }}
                />
              )}
              <BreakdownSelector
                mode="breakdowns-and-values"
                breakdownConfigs={breakdownConfigs}
                selectedBreakdowns={breakdownFilters}
                buttonContentComponent={({ isOpen }) => {
                  return breakdownFilters.length === 0 ? (
                    <>
                      <i
                        className={clsx(
                          "fa-regular fa-sharp fa-circle-plus mr-1.5 text-v2-action",
                          {
                            "text-white": isOpen,
                          },
                        )}
                      />
                      ADD FILTER
                    </>
                  ) : (
                    <i
                      className={clsx(
                        "fa-regular fa-sharp fa-add text-xs text-v2-primary font-bold",
                        {
                          "text-white": isOpen,
                        },
                      )}
                    />
                  );
                }}
                buttonClassName={clsx(
                  "button sm secondary-button font-bold bg-v2-primary text-v2-action border-v2-secondary",
                  {
                    "w-7 h-7 p-0": breakdownFilters.length > 0,
                  },
                )}
                buttonActiveClassName={clsx(
                  "button sm secondary-button font-bold bg-v2-action text-white border-v2-action",
                  { "w-7 h-7 p-0": breakdownFilters.length > 0 },
                )}
                menuPlacement="bottom-start"
                onSelectionChange={(values) => {
                  setBreakdownFilters(values);
                }}
              />
            </div>
          )}
          <Checkbox
            label="Hide inactive ads"
            value={activeStatus === CreativesStatusEnum.active}
            onChange={() => {
              const status =
                activeStatus === CreativesStatusEnum.active
                  ? null
                  : CreativesStatusEnum.active;
              setActiveStatus(status);
              prettyLogEvent.onClick({
                user,
                page: Pages.CREATIVE_ANALYTICS,
                additionalActionInfo: "hide-inactive-ads",
                data: status,
              });
            }}
            className="text-sm whitespace-nowrap"
          />
        </div>
        <AdControlBar />
      </div>
      <div className="flex flex-row">
        {creativeTypes.map((caType) => {
          const isActive = activeCreative?.id === caType.id;
          return (
            <CreativeAnalyticsLink
              isActive={isActive}
              key={caType.id}
              text={caType.name}
              onClick={() => {
                setCreativeType(caType.id === "all" ? null : caType.id);
                prettyLogEvent.onClick({
                  user,
                  page: Pages.CREATIVE_ANALYTICS,
                  domain: "Type Bar",
                  additionalActionInfo: "change-type-buttons",
                });
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

function AdControlBar() {
  const { selectedAdsByAdKey, setSelectedAdsByAdKey } = useReportBodyState();
  const { user } = useUser();
  const [isExpanded, setExpanded] = useCollapseTopBar();

  return (
    <div className="mr-5 flex flex-row items-center">
      <div className="flex flex-row items-center space-x-2 whitespace-nowrap">
        <div>
          <span className="font-bold">
            {Object.keys(selectedAdsByAdKey).length}
          </span>{" "}
          selected
        </div>
        <TertiaryButton
          onClick={() => {
            setSelectedAdsByAdKey({});
            prettyLogEvent.onClick({
              user,
              page: Pages.CREATIVE_ANALYTICS,
              additionalActionInfo: "remove-ad-selections",
            });
          }}
        >
          REMOVE SELECTIONS
        </TertiaryButton>
      </div>
      <div className="h-[2.5rem] border-l-[1.5px] mr-4 ml-3 border-v2-secondary" />
      <SortMetric />
      <Popover className="relative">
        {({ open, close }) => (
          <FilterMetricPopover isOpen={open} close={close} />
        )}
      </Popover>
      <PrimaryButton
        onClick={() => {
          const newExpanded = !isExpanded;
          setExpanded(newExpanded);
          prettyLogEvent.onClick({
            user,
            page: Pages.CREATIVE_ANALYTICS,
            additionalActionInfo: "hide-controls",
            data: { newExpanded },
          });
        }}
        className="ml-3"
      >
        {isExpanded ? "HIDE CONTROLS" : "SHOW CONTROLS"}
      </PrimaryButton>
    </div>
  );
}

const defaultClause = {
  metricClauses: [{} as CreativesMetricsFilterInput["metricClauses"][number]],
  clauseType: CreativesFilterClauseTypeEnum.AND,
};

function FilterMetricPopover({
  isOpen,
  close,
}: {
  isOpen: boolean;
  close: () => void;
}) {
  const { metricFilters, setMetricFilters } = useReportBodyState();

  const [unsavedMetricFilters, setUnsavedMetricFilters] =
    useState<Partial<CreativesMetricsFilterInput>>(metricFilters);

  const addMetric = useCallback(
    (
      index: number,
      metric: Omit<CreativesFilterMetricClauseInput, "operator" | "value">,
    ) => {
      setUnsavedMetricFilters((prev) => {
        const metricClauses = [...(prev.metricClauses ?? [])];
        if (!metricClauses[index]) {
          metricClauses.push({} as CreativesFilterMetricClauseInput);
        }
        metricClauses[index] = { ...metricClauses[index], ...metric };
        return { ...prev, metricClauses };
      });
    },
    [],
  );

  const addOperator = useCallback(
    (index: number, operator: CreativesFilterMetricClauseInput["operator"]) => {
      setUnsavedMetricFilters((prev) => {
        const metricClauses = [...(prev.metricClauses ?? [])];
        if (!metricClauses[index]) {
          metricClauses.push({} as CreativesFilterMetricClauseInput);
        }
        metricClauses[index] = { ...metricClauses[index], operator };
        return { ...prev, metricClauses };
      });
    },
    [],
  );

  const addValue = useCallback(
    (index: number, value: CreativesFilterMetricClauseInput["value"]) => {
      setUnsavedMetricFilters((prev) => {
        const metricClauses = [...(prev.metricClauses ?? [])];
        if (!metricClauses[index]) {
          metricClauses.push({} as CreativesFilterMetricClauseInput);
        }
        metricClauses[index] = { ...metricClauses[index], value };
        return { ...prev, metricClauses };
      });
    },
    [],
  );

  const addNewItem = useCallback(() => {
    setUnsavedMetricFilters((prev) => {
      const metricClauses = [...(prev.metricClauses ?? [])];
      metricClauses.push({} as CreativesFilterMetricClauseInput);
      return { ...prev, metricClauses };
    });
  }, []);

  const removeItem = useCallback((toRemoveIndex: number) => {
    setUnsavedMetricFilters((prev) => {
      const metricClauses = [] as CreativesFilterMetricClauseInput[];
      prev.metricClauses?.forEach((metric, index) => {
        if (index !== toRemoveIndex) {
          metricClauses.push(metric);
        }
      });

      return { ...prev, metricClauses };
    });
  }, []);

  useEffect(() => {
    isOpen &&
      setUnsavedMetricFilters(
        isEmpty(metricFilters) ? defaultClause : metricFilters,
      );
  }, [metricFilters, isOpen]);

  return (
    <>
      <PopoverButton
        className={clsx(
          "ml-1 flex flex-col border border-v2-secondary rounded-md h-8 w-8 items-center",
          "cursor-pointer justify-center",
          {
            "bg-primary border-transparent": !isEmpty(metricFilters),
          },
        )}
      >
        <i
          className={clsx("fa-sharp text-sm fa-regular fa-filter ", {
            "text-white": !isEmpty(metricFilters),
            "text-primary": isEmpty(metricFilters),
          })}
        />
      </PopoverButton>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
        show={isOpen}
      >
        <PopoverPanel
          static
          className={clsx(
            "absolute right-0 w-[20rem] z-[100] mt-3  bg-v2-primary",
            "rounded-lg elevation-1 flex flex-col px-7 py-5",
          )}
        >
          <button onClick={close} className="absolute top-2 right-3">
            <i className="fa-regular fa-sharp fa-xmark text-[1em] cursor-pointer" />
          </button>
          <div>
            <div className="overflow-y-auto max-h-[15rem] px-1">
              {unsavedMetricFilters?.metricClauses?.map((clause, index) => (
                <FilterMetricItem
                  key={index}
                  clause={clause}
                  index={index}
                  addMetric={addMetric}
                  addOperator={addOperator}
                  addValue={addValue}
                  removeItem={removeItem}
                  totalNumber={unsavedMetricFilters?.metricClauses?.length ?? 0}
                />
              ))}
            </div>
            <TertiaryButton
              onClick={addNewItem}
              className={clsx(
                "h-[2.5rem] mt-2 rounded-full mr-2 flex flex-row",
                "items-center  font-semibold",
              )}
            >
              <i className="mr-3 text-lg fa-regular fa-sharp fa-circle-plus text-v2-action" />
              <div className="text-primary">ADD FILTER</div>
            </TertiaryButton>
            <CombinationLogicSelector
              value={unsavedMetricFilters.clauseType}
              onChange={(value) =>
                setUnsavedMetricFilters((prev) => ({
                  ...prev,
                  clauseType: value,
                }))
              }
            />
            <SubmitButtons
              onRemove={() => {
                setUnsavedMetricFilters(defaultClause);
                setMetricFilters({} as CreativesMetricsFilterInput);
              }}
              onSubmit={() => {
                const metricClauses =
                  unsavedMetricFilters.metricClauses?.filter((m) => {
                    return (
                      !isNil(m.metric) && !isNil(m.operator) && !isNil(m.value)
                    );
                  });

                if (metricClauses?.length) {
                  setMetricFilters({
                    ...unsavedMetricFilters,
                    metricClauses: metricClauses.map((m) => ({
                      ...m,
                      value: Number(m.value),
                    })),
                  } as CreativesMetricsFilterInput);
                } else {
                  setMetricFilters({} as CreativesMetricsFilterInput);
                }

                close();
              }}
              disabled={Boolean(
                !unsavedMetricFilters.clauseType ||
                  unsavedMetricFilters?.metricClauses?.some(
                    (metricClause) =>
                      isNil(metricClause.customComposed) ||
                      isNil(metricClause.metric) ||
                      isNil(metricClause.operator) ||
                      isNil(metricClause.type) ||
                      isNil(metricClause.value) ||
                      String(metricClause?.value).trim() === "",
                  ),
              )}
            />
          </div>
        </PopoverPanel>
      </Transition>
    </>
  );
}

function SubmitButtons({
  onRemove,
  onSubmit,
  disabled,
}: {
  onRemove: () => void;
  onSubmit: () => void;
  disabled: boolean;
}) {
  return (
    <div className="flex flex-row justify-between mt-2">
      <SecondaryButton
        onClick={onRemove}
        className="h-[2.5rem] mt-2 rounded-full mr-2 flex flex-row items-center  font-semibold"
      >
        REMOVE
      </SecondaryButton>
      <PrimaryButton
        isDisabled={disabled}
        onClick={onSubmit}
        className="h-[2.5rem] mt-2 rounded-full mr-2 flex flex-row items-center  font-semibold"
      >
        APPLY
      </PrimaryButton>
    </div>
  );
}

const clauseTypeOptions = [
  {
    label: "AND",
    value: CreativesFilterClauseTypeEnum.AND,
  },
  {
    label: "OR",
    value: CreativesFilterClauseTypeEnum.OR,
  },
];

function CombinationLogicSelector({
  onChange,
  value,
}: {
  value: CreativesFilterClauseTypeEnum | undefined;
  onChange: (value: CreativesFilterClauseTypeEnum) => void;
}) {
  return (
    <div>
      <div className="text-sm flex flex-row justify-between mt-2">
        <div>COMBINATION LOGIC</div>
      </div>
      <div className="w-full mt-2">
        <Select<CreativesFilterClauseTypeEnum | undefined>
          onChange={(v) => v && onChange(v)}
          options={clauseTypeOptions}
          value={value}
          defaultValue={clauseTypeOptions[0].value}
          isSearchable
        />
      </div>
    </div>
  );
}

export const operatorOptions = [
  {
    label: "Greater than",
    value: CreativesFilterClauseValueOperatorsEnum.greaterThan,
  },
  {
    label: "Greater than or Equal to",
    value: CreativesFilterClauseValueOperatorsEnum.greaterThanEqualTo,
  },
  {
    label: "Less than",
    value: CreativesFilterClauseValueOperatorsEnum.lessThan,
  },
  {
    label: "Less than or Equal to",
    value: CreativesFilterClauseValueOperatorsEnum.lessThanOrEqualTo,
  },
  {
    label: "Equal to",
    value: CreativesFilterClauseValueOperatorsEnum.equalsTo,
  },
];

function FilterMetricItem({
  clause,
  index,
  addMetric,
  addOperator,
  addValue,
  removeItem,
  totalNumber,
}: {
  totalNumber: number;
  clause: CreativesFilterMetricClauseInput | undefined;
  index: number;
  removeItem: (index: number) => void;
  addMetric: (
    index: number,
    metric: Omit<CreativesFilterMetricClauseInput, "operator" | "value">,
  ) => void;
  addOperator: (
    index: number,
    operator: CreativesFilterMetricClauseInput["operator"],
  ) => void;
  addValue: (
    index: number,
    value: CreativesFilterMetricClauseInput["value"],
  ) => void;
}) {
  const [availableMetricsById] = useAvailableMetricsByIdAtom();
  const allMetrics = Object.values(availableMetricsById);

  const { attributionModel, attributionWindow } = useReportBodyState();
  return (
    <div className={index ? "mt-5" : "mt-2"}>
      <div className="text-sm flex flex-row justify-between">
        <div>FILTER {totalNumber > 1 && <>{index + 1}</>}</div>
        {totalNumber > 1 && (
          <i
            className="fa-regular fa-sharp fa-trash ml-auto cursor-pointer mr-2 text-danger"
            onClick={() => removeItem(index)}
          />
        )}
      </div>
      <div className="w-full mt-2">
        <Select<
          | Omit<CreativesFilterMetricClauseInput, "operator" | "value">
          | undefined
        >
          name="ca-metric"
          isSearchable
          value={clause}
          formatDisplayValue={(option) => {
            return option?.label ?? "";
          }}
          menuClassName="max-h-[10rem] w-[15rem]"
          placeholder="Metric..."
          comparator={(a, b) => a?.metric?.id === b?.metric?.id}
          options={[
            ...allMetrics.map((metric) => ({
              value: {
                customComposed: metric.composition.customComposed,
                metric: {
                  id: metric.id,
                  metric: metric.composition.metric,

                  details: {
                    type: "",
                    am: attributionModel,
                    aw: attributionWindow,
                    accountingMode: "accrual",
                    ...metric.composition.details,
                  },
                },

                type: metric.composition.type,
              },
              label: metric.name,
            })),
            {
              value: {
                customComposed: true,
                metric: { id: "date", metric: "date" },
                type: CreativeMetricType.base,
              },
              label: "Date",
            },
          ]}
          onChange={(value) => {
            value && addMetric(index, value);
          }}
        />
      </div>
      <div className="w-full mt-2">
        <Select
          isSearchable={true}
          onChange={function (value) {
            value && addOperator(index, value);
          }}
          placeholder="Operator..."
          options={operatorOptions}
          value={clause?.operator}
          menuClassName="max-h-[10rem] w-[15rem]"
        />
      </div>
      <TextInput
        {...(clause?.value && { defaultValue: String(clause?.value) })}
        className="w-full mt-2"
        inputClassName="font-light"
        placeholder="Filter value"
        type="number"
        value={String(clause?.value)}
        onChange={(e) => addValue(index, e.target.value)}
      />
    </div>
  );
}
function SortMetric() {
  const { sorting, setSorting, sortingOrder, setSortingOrder } =
    useReportBodyState();
  const [availableMetricsById] = useAvailableMetricsByIdAtom();

  const allMetrics = Object.values(availableMetricsById);
  const { user } = useUser();

  const isDescending = sortingOrder === CreativesSortingOrder.desc;
  const ascArrow = {
    "fa-light": isDescending,
    "fa-solid": !isDescending,
  };
  const descArrow = {
    "fa-light": !isDescending,
    "fa-solid": isDescending,
  };

  return (
    <div className="flex flex-row items-center">
      <span className="mr-2 text-sm text-v2-primary">SORT</span>
      <div className="w-[12rem] mr-1">
        <Select<CreativeSorting>
          name="ca-sorting"
          isSearchable
          value={sorting}
          formatDisplayValue={(option) => {
            return option?.label ?? "";
          }}
          comparator={(a, b) => a.id === b.id}
          options={[
            ...allMetrics.map((metric) => ({
              value: {
                customComposed: metric.composition.customComposed,
                metric: {
                  id: metric.id,
                  metric: metric.composition.metric,
                  ...(metric.composition.details && {
                    details: metric.composition.details,
                  }),
                },
                id: metric.id,
                type: metric.composition.type,
              },
              label: metric.name,
            })),
            {
              value: {
                customComposed: true,
                metric: { id: "date", metric: "date" },
                id: "date",
                type: CreativeMetricType.base,
              },
              label: "Date",
            },
          ]}
          onChange={(value) => {
            const newSorting = {
              customComposed: value.customComposed,
              metric: value.metric,
              id: value.id,
              type: value.type,
            };
            const newSortingOrder =
              availableMetricsById[value.id]?.isPositiveChangeGood ?? true
                ? CreativesSortingOrder.desc
                : CreativesSortingOrder.asc;
            setSorting(newSorting);
            setSortingOrder(newSortingOrder);
            prettyLogEvent.onSelect({
              user,
              page: Pages.CREATIVE_ANALYTICS,
              additionalActionInfo: "sorting-selector",
              data: { newSorting, newSortingOrder },
            });
          }}
        />
      </div>
      <div
        className={clsx(
          "flex flex-col border border-v2-secondary rounded-md h-8 w-8",
          "items-center cursor-pointer",
        )}
        onClick={() => {
          const newSortingOrder =
            sortingOrder === CreativesSortingOrder.desc
              ? CreativesSortingOrder.asc
              : CreativesSortingOrder.desc;
          setSortingOrder(newSortingOrder);
          prettyLogEvent.onSelect({
            user,
            page: Pages.CREATIVE_ANALYTICS,
            additionalActionInfo: "sorting-order",
            data: { newSortingOrder },
          });
        }}
      >
        <i
          className={clsx(
            "fa-sharp fa-sort-up h-[0.025rem] mt-[0.4rem] text-primary",
            ascArrow,
          )}
        />
        <i
          className={clsx(
            "fa-sharp fa-sort-down h-[0.025rem] mt-0 text-primary",
            descArrow,
          )}
        />
      </div>
    </div>
  );
}

export function CreativeAnalyticsLink({
  isActive,
  onClick,
  text,
}: {
  text: string;
  isActive: boolean;
  onClick?: () => void;
}) {
  return (
    <div
      className={clsx("px-4 cursor-pointer mt-3", {
        "text-v2-secondary": !isActive,
        "text-v2-action": isActive,
      })}
      onClick={onClick}
    >
      <div className="mx-2 max-w-[10rem] truncate">{text}</div>
      {isActive && (
        <div className="border-v2-action bottom-0 border-t-2 mt-2" />
      )}
    </div>
  );
}

function useFetchCreativeTypes() {
  return [
    { name: "All", id: "all" },
    { name: "Image", id: CreativesTypeEnum.IMAGE },
    { name: "Video", id: CreativesTypeEnum.VIDEO },
  ] as const;
}
